import { useLayoutEffect, useRef } from "react";
import { css } from "goober";

import { TOOLTIP_MAX_WIDTH, TOOLTIP_OFFSET } from "@/app/constants.mjs";
import { appSelector, internalContainerClass } from "@/util/constants.mjs";
import { devDebug } from "@/util/dev.mjs";
import globals from "@/util/global-whitelist.mjs";
import { durationToMs, setOrRemoveProperty } from "@/util/helpers.mjs";
import nextFrame from "@/util/next-frame.mjs";

export const TOOLTIP_FN_PROP = "_tooltipFn";

const cssTooltipContainer = () => css`
  --bg: var(--shade10, black);

  max-width: ${TOOLTIP_MAX_WIDTH};

  position: absolute;
  pointer-events: none;
  z-index: 999;
  background: var(--bg);
  border-radius: var(--br);
  color: var(--color, var(--shade0));
  padding: var(--sp-3);

  &.tooltip-plain {
    background: unset;
    border-radius: unset;
    color: unset;
    padding: unset;
  }

  transition:
    opacity var(--transition-duration-short) var(--ease-out-expo),
    transform var(--transition-duration-short) var(--ease-out-back) !important;

  &.exit-to {
    opacity: 0;
    transform: scale(0.93);
  }

  &.enter-from {
    opacity: 0;
    transform: scale(0.93);
  }

  &[data-size="sm"] {
    font-size: var(--sp-3e);
    padding: var(--sp-1) var(--sp-2);
  }
`;

// ## Rationale: all open-source solutions we've looked at fail to do 2 things
// we want from a tooltip:
//
// - use html attributes (the DOM is the API)
// - handle dynamic content being added/removed
//
// ## Interface:
//
// ### data-
// - `tooltip`: tooltip content. can be rendered html
// - `tip`: alias for `tooltip`
// - `place`: tooltip placement. can be `top`, `bottom`, `left`, `right`
// - `event`: tooltip event. can be `click`
// ### html tooltip content
// - class `tooltip-plain`: tooltip content will be rendered without tooltip styles (padding, bg color)

let tooltipWrapper: HTMLElement;

function getInternalContainer() {
  return globals.document.querySelector(appSelector);
}

function getTooltipData(node: HTMLElement | SVGElement) {
  let currentNode = node,
    value: string,
    placement: string,
    tooltipEvent: string,
    background: string,
    color: string,
    size: string;
  while (currentNode) {
    const {
      dataset: {
        tooltip,
        tip,
        place,
        event,
        tipSize = "md",
        tipBg,
        tipColor,
      } = {},
    } = currentNode;
    value = tooltip ?? tip;
    if (currentNode[TOOLTIP_FN_PROP]) {
      value = currentNode[TOOLTIP_FN_PROP]();
    }
    placement = place;
    tooltipEvent = event;
    size = tipSize;
    background = tipBg;
    color = tipColor;
    if (value) {
      if (!tooltipWrapper)
        tooltipWrapper = globals.document.createElement("div");
      tooltipWrapper.classList.add(internalContainerClass);
      break;
    }
    currentNode = currentNode.parentElement;
  }
  if (currentNode && !getInternalContainer().contains(tooltipWrapper))
    getInternalContainer().appendChild(tooltipWrapper);
  return {
    node: currentNode,
    content: value,
    placement,
    event: tooltipEvent,
    size,
    background,
    color,
  };
}

const defaultPlacement = "top";
let tooltipElement: HTMLElement = null;
let tooltipTarget: HTMLElement = null;
let tooltipPlacement: string = defaultPlacement;
const tooltipResizedObserver =
  typeof ResizeObserver === "undefined"
    ? null
    : new ResizeObserver(() => {
        updatePosition(tooltipPlacement);
      });
const removedTargetObserver =
  typeof MutationObserver === "undefined"
    ? null
    : new MutationObserver((mutations) => {
        for (const { removedNodes } of mutations) {
          for (const node of Array.from(removedNodes)) {
            if (node.contains(tooltipTarget)) return hide();
          }
        }
      });
// does not allow dynamically set content, only for removal
const tooltipAttributeObserver =
  typeof MutationObserver === "undefined"
    ? null
    : new MutationObserver((mutations) => {
        for (const { attributeName, target } of mutations) {
          if (!(target instanceof HTMLElement)) continue;
          if (
            attributeName === "data-tooltip" ||
            attributeName === "data-tip"
          ) {
            if (!target.getAttribute(attributeName)) hide();
          }
        }
      });

const setupObservers = () => {
  removedTargetObserver.observe(globals.document.body, {
    childList: true,
    subtree: true,
  });
  if (!tooltipElement || tooltipElement.nodeType !== Node.ELEMENT_NODE) {
    devDebug("Invalid tooltipElement");
    return;
  }
  if (!tooltipTarget || tooltipTarget.nodeType !== Node.ELEMENT_NODE) {
    devDebug("Invalid tooltipTarget");
    return;
  }
  tooltipResizedObserver.observe(tooltipElement);
  tooltipAttributeObserver.observe(tooltipTarget, {
    attributes: true,
    attributeFilter: ["data-tooltip", "data-tip"],
  });
};

const teardownObservers = () => {
  tooltipResizedObserver.disconnect();
  removedTargetObserver.disconnect();
  tooltipAttributeObserver.disconnect();
};

const events = ["mouseover", "mouseout", "click"];
export function attachListeners(rootNode = globals.document?.body) {
  events.forEach((event) => {
    rootNode.addEventListener(event, handleEventAction);
  });
}

export function removeListeners(rootNode = globals.document?.body) {
  events.forEach((event) => {
    rootNode?.removeEventListener(event, handleEventAction);
  });
}
function handleEventAction(e: MouseEvent) {
  if (!(e.target instanceof HTMLElement || e.target instanceof SVGElement))
    return;

  const target = e.target;
  const { node, event } = getTooltipData(target);
  if (!node) return;
  switch (e.type) {
    case "click":
      if (event !== "click") break;
      show(node);
      break;
    case "mouseover":
      if (event === "click") break;
      show(node);
      break;
    case "mouseout":
      hide();
      break;
  }
}

let removeTimer: Timeout;
function hide() {
  if (!tooltipElement) return;
  teardownObservers();
  // transition out
  tooltipElement.classList.add("exit-to");
  const duration = getComputedStyle(tooltipElement).getPropertyValue(
    "transition-duration",
  );
  const node = tooltipElement;
  removeTimer = setTimeout(() => {
    // safeguard for removal to be intercepted by another show()
    if (!node?.classList.contains("exit-to")) return;
    node.remove();
    tooltipElement = null;
  }, durationToMs(duration));
}

function getPosition(placement: string) {
  const coords = tooltipTarget.getBoundingClientRect();

  const sizeTarget = tooltipElement;

  let left = `${
    (coords.left + coords.right - tooltipElement.offsetWidth) / 2
  }px`;

  let top = `${
    (coords.top + coords.bottom - tooltipElement.offsetHeight) / 2
  }px`;

  switch (placement) {
    case "left":
      left = `${
        coords.left - tooltipElement.offsetWidth
      }px - ${TOOLTIP_OFFSET}`;
      break;
    case "right":
      left = `${coords.right}px + ${TOOLTIP_OFFSET}`;
      break;
    case "top":
      top = `${coords.top - sizeTarget.offsetHeight}px - ${TOOLTIP_OFFSET}`;
      break;
    case "bottom":
    default:
      top = `${coords.bottom}px + ${TOOLTIP_OFFSET}`;
  }

  const marginToScreen = TOOLTIP_OFFSET || "0px";
  // clamp to screen extremes
  // use css to clamp because we are working with mixed units
  top = `clamp(${marginToScreen}, ${top}, ${
    globals.innerHeight - sizeTarget.offsetHeight
  }px - ${marginToScreen})`;

  left = `clamp(${marginToScreen}, ${left}, ${
    globals.innerWidth - sizeTarget.offsetWidth
  }px - ${marginToScreen})`;

  return { left, top };
}

function updatePosition(placement) {
  const { left, top } = getPosition(placement);
  tooltipElement.style.left = left;
  tooltipElement.style.top = top;
}

function show(targetNode) {
  // cancel remove effect
  clearTimeout(removeTimer);
  // retrieve data from target node
  tooltipTarget = targetNode;
  const { content, placement, size, background, color } =
    getTooltipData(tooltipTarget);
  tooltipPlacement = placement || defaultPlacement;
  const container = tooltipWrapper;

  // create tooltip
  if (!tooltipElement) {
    tooltipElement = globals.document.createElement("div");
    tooltipElement.classList.add(cssTooltipContainer(), tooltipPlacement);
  }

  tooltipElement.dataset.size = size;

  setOrRemoveProperty(tooltipElement.style, "--bg", background);
  setOrRemoveProperty(tooltipElement.style, "--color", color);

  // assign inner content
  tooltipElement.innerHTML = content;
  if (tooltipElement.firstElementChild?.classList.contains("tooltip-plain"))
    tooltipElement.classList.add("tooltip-plain");

  // transitions
  tooltipElement.classList.add("enter-from");
  tooltipElement.classList.remove("exit-to"); // exists if prev tooltip was exiting
  nextFrame(() => {
    tooltipElement?.classList.remove("enter-from");
  });

  // attach to body and update position
  if (!container.contains(tooltipElement)) container.append(tooltipElement);
  setupObservers();
  updatePosition(tooltipPlacement);
}

export function useLazyTooltip(fn) {
  const tooltipRef = useRef(null);
  useLayoutEffect(() => {
    const { current } = tooltipRef;
    if (!current) return;
    current[TOOLTIP_FN_PROP] = fn;
  }, [tooltipRef, fn]);
  return tooltipRef;
}
